/**
 * AABB Collision detection for cuboids
 * Prevents cuboids from intersecting each other
 */
export default class CollisionManager {
  /**
   * Create a new collision manager
   * @param {World} world - The world instance containing cuboids
   */
  constructor(world) {
    this.world = world;
  }

  /**
   * Check if a proposed AABB collides with any existing cuboid
   * Uses strict inequality so touching faces are allowed (not considered collision)
   *
   * @param {number} x - X position
   * @param {number} y - Y position (height)
   * @param {number} z - Z position (depth)
   * @param {number} width - Width (X-axis)
   * @param {number} height - Height (Y-axis)
   * @param {number} depth - Depth (Z-axis)
   * @param {string|null} excludeKey - Cuboid key to exclude from check (for move/resize)
   * @return {boolean} True if collision detected
   */
  checkCollision(x, y, z, width, height, depth, excludeKey = null) {
    const proposedBox = {
      minX: x,
      minY: y,
      minZ: z,
      maxX: x + width,
      maxY: y + height,
      maxZ: z + depth
    };

    for (const [key, cuboid] of this.world.cuboids) {
      // Skip the cuboid we're moving/resizing
      if (key === excludeKey) continue;

      const existingBox = {
        minX: cuboid.x,
        minY: cuboid.y,
        minZ: cuboid.z,
        maxX: cuboid.x + cuboid.width,
        maxY: cuboid.y + cuboid.height,
        maxZ: cuboid.z + cuboid.depth
      };

      if (this.aabbIntersects(proposedBox, existingBox)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Standard AABB intersection test
   * Uses strict inequality (< and >) so touching faces don't count as intersection
   *
   * @param {Object} a - First box {minX, minY, minZ, maxX, maxY, maxZ}
   * @param {Object} b - Second box {minX, minY, minZ, maxX, maxY, maxZ}
   * @return {boolean} True if boxes intersect (overlap)
   */
  aabbIntersects(a, b) {
    return (
      a.minX < b.maxX && a.maxX > b.minX &&
      a.minY < b.maxY && a.maxY > b.minY &&
      a.minZ < b.maxZ && a.maxZ > b.minZ
    );
  }

  /**
   * Find all cuboids that would collide with a proposed AABB
   * Useful for debugging or showing which cuboids are blocking
   *
   * @param {number} x - X position
   * @param {number} y - Y position (height)
   * @param {number} z - Z position (depth)
   * @param {number} width - Width
   * @param {number} height - Height
   * @param {number} depth - Depth
   * @param {string|null} excludeKey - Cuboid key to exclude
   * @return {Cuboid[]} Array of colliding cuboids
   */
  findCollisions(x, y, z, width, height, depth, excludeKey = null) {
    const collisions = [];
    const proposedBox = {
      minX: x,
      minY: y,
      minZ: z,
      maxX: x + width,
      maxY: y + height,
      maxZ: z + depth
    };

    for (const [key, cuboid] of this.world.cuboids) {
      if (key === excludeKey) continue;

      const existingBox = {
        minX: cuboid.x,
        minY: cuboid.y,
        minZ: cuboid.z,
        maxX: cuboid.x + cuboid.width,
        maxY: cuboid.y + cuboid.height,
        maxZ: cuboid.z + cuboid.depth
      };

      if (this.aabbIntersects(proposedBox, existingBox)) {
        collisions.push(cuboid);
      }
    }

    return collisions;
  }

  /**
   * Check if a proposed AABB collides with any character in the world
   * Prevents placing/moving cuboids into characters
   *
   * @param {number} x - X position
   * @param {number} y - Y position (height)
   * @param {number} z - Z position (depth)
   * @param {number} width - Width (X-axis)
   * @param {number} height - Height (Y-axis)
   * @param {number} depth - Depth (Z-axis)
   * @return {boolean} True if collision with any character detected
   */
  checkCharacterCollision(x, y, z, width, height, depth) {
    const proposedBox = {
      minX: x,
      minY: y,
      minZ: z,
      maxX: x + width,
      maxY: y + height,
      maxZ: z + depth
    };

    for (const character of this.world.characters.values()) {
      const charBox = character.getAABB();
      if (this.aabbIntersects(proposedBox, charBox)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a position is valid (no collision and positive dimensions)
   *
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {number} z - Z position
   * @param {number} width - Width (must be >= 1)
   * @param {number} height - Height (must be >= 1)
   * @param {number} depth - Depth (must be >= 1)
   * @param {string|null} excludeKey - Cuboid key to exclude
   * @return {boolean} True if position is valid
   */
  isValidPlacement(x, y, z, width, height, depth, excludeKey = null, skipCharacterCheck = false) {
    // Check minimum dimensions
    if (width < 1 || height < 1 || depth < 1) {
      return false;
    }

    // Check for cuboid collisions
    if (this.checkCollision(x, y, z, width, height, depth, excludeKey)) {
      return false;
    }

    // Skip character collision for noCollision cuboids (water, gas, etc.)
    if (!skipCharacterCheck && this.checkCharacterCollision(x, y, z, width, height, depth)) {
      return false;
    }

    return true;
  }
}
